/**
 * <stone-options> — Size + Type selectors for Shopify-product moissanite PDPs.
 *
 * Fetches sibling products (same shape) from a collection via Storefront API,
 * groups them into Size and Type dropdowns, navigates on change.
 *
 * Required attributes:
 *   collection-handle, shape, carat, measurement, grade, color, clarity, current-handle
 * Optional:
 *   stone-type (defaults to 'Moissanite')
 */

const QUERY = `
  query stoneOptions($handle: String!, $filters: [ProductFilter!], $after: String) {
    collection(handle: $handle) {
      products(first: 250, filters: $filters, after: $after) {
        edges {
          node {
            handle
            title
            shape:       metafield(namespace: "custom", key: "stone_shape")         { value }
            carat:       metafield(namespace: "custom", key: "stone_carat_weight")  { value }
            measurement: metafield(namespace: "custom", key: "stone_measurement")   { value }
            grade:       metafield(namespace: "custom", key: "stone_grade")         { value }
            color:       metafield(namespace: "custom", key: "stone_color")         { value }
            clarity:     metafield(namespace: "custom", key: "stone_clarity")       { value }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  }
`;

if (!customElements.get('stone-options')) {
  class StoneOptions extends HTMLElement {
    async connectedCallback() {
      if (this._loaded) return;
      this._loaded = true;

      this.shape       = this.getAttribute('shape') || '';
      this.carat       = this.getAttribute('carat') || '';
      this.measurement = this.getAttribute('measurement') || '';
      this.grade       = this.getAttribute('grade') || '';
      this.color       = this.getAttribute('color') || '';
      this.clarity     = this.getAttribute('clarity') || '';
      this.currentHandle = this.getAttribute('current-handle') || '';
      this.collectionHandle = this.getAttribute('collection-handle') || 'moissanites-1';
      this.stoneType = this.getAttribute('stone-type') || 'Moissanite';
      this.mode = (this.getAttribute('mode') || '').toLowerCase();

      try {
        await this.waitForApi();
        const products = await this.fetchSiblings();
        this.render(products);
      } catch (err) {
        console.warn('stone-options failed:', err);
      }
    }

    waitForApi() {
      return new Promise((resolve) => {
        if (window.theme?.shopify?.executeQuery) return resolve();
        const t = setInterval(() => {
          if (window.theme?.shopify?.executeQuery) { clearInterval(t); resolve(); }
        }, 50);
        setTimeout(() => { clearInterval(t); resolve(); }, 5000);
      });
    }

    async fetchSiblings() {
      // Gemstone groups by variety (stone_type) across all shapes, so it omits the
      // shape filter; moissanite keeps the same-shape filter for size/type variants.
      const filters = [
        { productMetafield: { namespace: 'custom', key: 'stone_type',  value: this.stoneType } },
      ];
      if (this.mode !== 'gemstone') {
        filters.push({ productMetafield: { namespace: 'custom', key: 'stone_shape', value: this.shape } });
      }
      // Paginate: a variety (e.g. Blue Sapphire) can have 250+ products across all
      // shapes/grades/sizes. A single page would drop the current shape/grade/size.
      const products = [];
      let after = null;
      for (let page = 0; page < 8; page++) {
        const data = await window.theme.shopify.executeQuery(QUERY, {
          handle: this.collectionHandle,
          filters,
          after,
        });
        const conn = data?.collection?.products;
        const edges = conn?.edges || [];
        edges.forEach(e => products.push({
          handle: e.node.handle,
          title: e.node.title,
          shape:       e.node.shape?.value || '',
          carat:       e.node.carat?.value || '',
          measurement: e.node.measurement?.value || '',
          grade:       e.node.grade?.value || '',
          color:       e.node.color?.value || '',
          clarity:     e.node.clarity?.value || '',
        }));
        if (!conn?.pageInfo?.hasNextPage) break;
        after = conn.pageInfo.endCursor;
      }
      return products;
    }

    render(products) {
      if (this.mode === 'gemstone') return this.renderGemstone(products);

      // Sizes: products matching grade + color + clarity, varying measurement/carat.
      const sizes = products
        .filter(p => p.grade === this.grade && p.color === this.color && p.clarity === this.clarity && p.measurement)
        .sort((a, b) => parseFloat(a.carat) - parseFloat(b.carat))
        .filter((p, i, arr) => arr.findIndex(x => x.measurement === p.measurement) === i);

      // Types: products matching measurement, varying grade/color/clarity.
      const types = products
        .filter(p => p.measurement === this.measurement && p.grade)
        .filter((p, i, arr) => arr.findIndex(x => `${x.grade}|${x.color}|${x.clarity}` === `${p.grade}|${p.color}|${p.clarity}`) === i);

      const sizeOpts = sizes.map(p =>
        `<option value="${p.handle}" ${p.handle === this.currentHandle ? 'selected' : ''}>${p.measurement} (${p.carat}ct)</option>`
      ).join('');

      const typeOpts = types.map(p =>
        `<option value="${p.handle}" ${p.handle === this.currentHandle ? 'selected' : ''}>${p.grade} (${p.color} Color, ${p.clarity} Clarity)</option>`
      ).join('');

      this.innerHTML = `
        <form data-options-form class="flex flex-col gap-1">
          ${sizes.length > 1 ? `
          <div class="mb-1">
            <div class="flex justify-between items-center py-3">
              <label for="stone-size" class="normal-case text-base tracking-normal m-0">Size</label>
            </div>
            <select id="stone-size" name="size" data-stone-select required>${sizeOpts}</select>
          </div>` : ''}
          ${types.length > 1 ? `
          <div class="mb-1">
            <div class="flex justify-between items-center py-3">
              <label for="stone-type" class="normal-case text-base tracking-normal m-0">Type</label>
            </div>
            <select id="stone-type" name="type" data-stone-select required>${typeOpts}</select>
          </div>` : ''}
        </form>
      `;

      this.bindNav();
    }

    /**
     * Gemstone grouping: Shape (image tiles) + Grade + Size, cascading on the
     * current selection, mirroring the legacy gemstone PDP. Siblings share the
     * same variety (stone_type); shapes/grades/sizes are derived client-side.
     */
    renderGemstone(products) {
      const slug = (s) => String(s || '').toLowerCase().replaceAll(' ', '-');

      // Shapes: distinct, ordered by first appearance. Representative handle prefers
      // keeping the current grade + measurement so switching shape stays comparable.
      const shapeMap = new Map();
      products.forEach(p => {
        if (!p.shape) return;
        if (!shapeMap.has(p.shape)) shapeMap.set(p.shape, []);
        shapeMap.get(p.shape).push(p);
      });
      const pickRep = (list) =>
        list.find(p => p.grade === this.grade && p.measurement === this.measurement) ||
        list.find(p => p.grade === this.grade) ||
        list[0];
      const shapes = Array.from(shapeMap.entries()).map(([shape, list]) => ({
        shape,
        handle: pickRep(list).handle,
        current: shape === this.shape,
      }));

      // Grades: within the current shape. Representative handle keeps current measurement.
      const curShape = products.filter(p => p.shape === this.shape);
      const gradeMap = new Map();
      curShape.forEach(p => {
        if (!p.grade) return;
        if (!gradeMap.has(p.grade)) gradeMap.set(p.grade, []);
        gradeMap.get(p.grade).push(p);
      });
      const grades = Array.from(gradeMap.entries()).map(([grade, list]) => ({
        grade,
        handle: (list.find(p => p.measurement === this.measurement) || list[0]).handle,
        selected: grade === this.grade,
      }));

      // Sizes: within the current shape + grade, distinct measurement, ascending carat.
      const seen = new Set();
      const sizes = curShape
        .filter(p => p.grade === this.grade && p.measurement)
        .sort((a, b) => parseFloat(a.carat) - parseFloat(b.carat))
        .filter(p => (seen.has(p.measurement) ? false : seen.add(p.measurement)));

      const shapeTiles = shapes.map(s => `
        <label class="mb-0 cursor-pointer w-full hover:underline has-[input:checked]:bg-base-50 has-[input:checked]:font-medium has-[input:checked]:outline has-[input:checked]:outline-2 has-[input:checked]:outline-black has-[input:checked]:relative has-[input:checked]:z-10">
          <input type="radio" class="peer sr-only" name="shape" value="${s.handle}" data-stone-nav ${s.current ? 'checked' : ''} />
          <span class="flex flex-col items-center justify-center gap-1 py-2 peer-checked:font-medium" style="pointer-events:none;">
            <span class="size-6 bg-cover bg-center bg-no-repeat option-image-center-stone-shape-${slug(s.shape)}"></span>
            <span class="line-clamp-2 text-xs text-center">${s.shape}</span>
          </span>
        </label>
      `).join('');

      const gradeOpts = grades.map(g =>
        `<option value="${g.handle}" ${g.selected ? 'selected' : ''}>${g.grade}</option>`
      ).join('');

      const sizeOpts = sizes.map(p =>
        `<option value="${p.handle}" ${p.handle === this.currentHandle ? 'selected' : ''}>${p.measurement} (${p.carat}ct)</option>`
      ).join('');

      this.innerHTML = `
        <form data-options-form class="flex flex-col gap-1">
          <div class="flex justify-between items-center py-3">
            <span class="normal-case text-base tracking-normal m-0">Shape</span>
          </div>
          <div class="option-tile mb-1" data-columns-count="4">
            <div class="option-tile_options grid grid-cols-4 relative border-l border-base-border [&>label]:border-r [&>label]:border-b [&>label]:border-base-border [&>label:nth-child(-n+4)]:border-t">
              ${shapeTiles}
            </div>
          </div>
          ${grades.length ? `
          <div class="mb-1">
            <div class="flex justify-between items-center py-3">
              <label for="stone-grade" class="normal-case text-base tracking-normal m-0">Grade</label>
            </div>
            <select id="stone-grade" name="grade" data-stone-nav required>${gradeOpts}</select>
          </div>` : ''}
          ${sizes.length ? `
          <div class="mb-1">
            <div class="flex justify-between items-center py-3">
              <label for="stone-size" class="normal-case text-base tracking-normal m-0">Size</label>
            </div>
            <select id="stone-size" name="size" data-stone-nav required>${sizeOpts}</select>
          </div>` : ''}
        </form>
      `;

      this.bindNav();
    }

    bindNav() {
      this.querySelectorAll('[data-stone-nav], [data-stone-select]').forEach(el => {
        el.addEventListener('change', (e) => {
          const handle = e.target.value;
          if (handle && handle !== this.currentHandle) {
            window.location.href = `/products/${handle}`;
          }
        });
      });
    }
  }
  customElements.define('stone-options', StoneOptions);
}
